import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Plus, MagnifyingGlass, X } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const EMPTY = {
  name: "", type: "B2C", company: "", phone: "", whatsapp: "", email: "",
  address: "", city: "", country: "Pakistan", currency_pref: "PKR",
  tags: [], notes: "",
};

export default function Customers() {
  const { hasRole } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/customers").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const canManage = hasRole("admin", "sales", "operations");

  const startEdit = (c) => {
    setEditing(c);
    setForm({ ...EMPTY, ...c });
    setOpen(true);
  };
  const startNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/customers/${editing.id}`, form);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", form);
        toast.success("Customer created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const filtered = items.filter(
    (c) =>
      c.name?.toLowerCase().includes(q.toLowerCase()) ||
      c.code?.toLowerCase().includes(q.toLowerCase()) ||
      c.phone?.includes(q),
  );

  return (
    <div className="space-y-5" data-testid="customers-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mini">Customer Directory</div>
          <h1 className="text-3xl font-bold tracking-tight mt-1">Customers</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              data-testid="customer-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, code, phone…"
              className="h-9 pl-8 w-72 rounded-sm"
            />
          </div>
          {canManage && (
            <Button onClick={startNew} data-testid="add-customer-btn"
                    className="h-9 rounded-sm bg-black hover:bg-zinc-800 text-white">
              <Plus size={14} className="mr-1" /> New Customer
            </Button>
          )}
        </div>
      </div>

      <div className="card-sharp overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 sticky top-0">
            <tr className="text-left">
              <th className="label-mini px-4 py-3">Code</th>
              <th className="label-mini px-4 py-3">Name</th>
              <th className="label-mini px-4 py-3">Type</th>
              <th className="label-mini px-4 py-3">Contact</th>
              <th className="label-mini px-4 py-3">City</th>
              <th className="label-mini px-4 py-3 text-right">Balance (PKR)</th>
              <th className="label-mini px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`customer-row-${c.code}`}>
                <td className="px-4 py-3 font-mono text-xs">{c.code}</td>
                <td className="px-4 py-3 font-medium">
                  <div>{c.name}</div>
                  {c.company && <div className="text-[11px] text-zinc-500">{c.company}</div>}
                </td>
                <td className="px-4 py-3">
                  <span className="status-badge" style={{ background: c.type === "B2B" ? "#E0E7FF" : "#F4F4F5", color: c.type === "B2B" ? "#0020C2" : "#52525B", borderColor: c.type === "B2B" ? "#0020C2" : "#E4E4E7" }}>{c.type}</span>
                </td>
                <td className="px-4 py-3 font-mono text-xs">
                  <div>{c.phone || "—"}</div>
                  <div className="text-zinc-500">{c.email || ""}</div>
                </td>
                <td className="px-4 py-3 text-xs">{c.city || "—"}</td>
                <td className="px-4 py-3 text-right font-mono tnum">{fmtMoney(c.balance || 0, "")}</td>
                <td className="px-4 py-3 text-right">
                  {canManage && (
                    <button onClick={() => startEdit(c)} className="text-xs font-semibold underline mr-3" data-testid={`edit-customer-${c.code}`}>
                      Edit
                    </button>
                  )}
                  <Link to={`/customers/${c.id}`} className="text-xs font-semibold underline" data-testid={`view-customer-${c.code}`}>
                    View
                  </Link>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-10 text-zinc-500 text-sm">No customers found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="customer-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Customer" : "New Customer"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name" required>
              <Input data-testid="cust-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm h-9" />
            </Field>
            <Field label="Type">
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="rounded-sm h-9" data-testid="cust-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="B2C">B2C — Individual</SelectItem>
                  <SelectItem value="B2B">B2B — Business</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {form.type === "B2B" && (
              <Field label="Company">
                <Input value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} className="rounded-sm h-9" />
              </Field>
            )}
            <Field label="Phone">
              <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm h-9" />
            </Field>
            <Field label="WhatsApp">
              <Input value={form.whatsapp || ""} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="rounded-sm h-9" />
            </Field>
            <Field label="Email">
              <Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm h-9" />
            </Field>
            <Field label="City">
              <Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-sm h-9" />
            </Field>
            <Field label="Country">
              <Input value={form.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value })} className="rounded-sm h-9" />
            </Field>
            <Field label="Currency Preference">
              <Select value={form.currency_pref} onValueChange={(v) => setForm({ ...form, currency_pref: v })}>
                <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PKR">PKR</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="CNY">CNY</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <div className="col-span-2">
              <Field label="Address">
                <Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-sm h-9" />
              </Field>
            </div>
            <div className="col-span-2">
              <Field label="Notes">
                <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                          className="w-full border border-zinc-200 rounded-sm p-2 text-sm h-20" />
              </Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-customer-btn" onClick={save} disabled={saving || !form.name} className="rounded-sm bg-black text-white hover:bg-zinc-800">
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <label className="label-mini">{label}{required && <span className="text-red-600">*</span>}</label>
    <div className="mt-1">{children}</div>
  </div>
);
